import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  IconButton,
  Grid,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { format, differenceInDays } from 'date-fns';
import { Task } from '@/types';

interface TaskGridProps {
  tasks: Task[];
  onEdit: (task: Task) => void;
  onDelete: (taskId: string) => void;
}

export const TaskGrid: React.FC<TaskGridProps> = ({
  tasks,
  onEdit,
  onDelete
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_process':
        return 'primary';
      case 'frozen':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTaskAge = (createdAt?: string) => {
    if (!createdAt) return '';
    const days = differenceInDays(new Date(), new Date(createdAt));
    if (days === 0) return 'Created today';
    if (days === 1) return 'Created yesterday';
    return `Created ${days} days ago`;
  };

  return (
    <Grid container spacing={2}>
      {tasks.map((task) => (
        <Grid item xs={12} sm={6} md={4} key={task.id}>
          <Paper
            sx={{
              p: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 3
              }
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  {task.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    mb: 1
                  }}
                >
                  {task.description}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  label={task.status.replace('_', ' ')}
                  color={getStatusColor(task.status)}
                  size="small"
                />
                <Chip
                  label={task.priority}
                  color={getPriorityColor(task.priority)}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={task.category}
                  size="small"
                  variant="outlined"
                />
              </Stack>

              <Box sx={{ mt: 'auto' }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                >
                  <Tooltip title={getTaskAge(task.createdAt)}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <TimeIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        Due: {format(new Date(task.dueDate), 'MMM d')}
                      </Typography>
                    </Stack>
                  </Tooltip>

                  <Stack direction="row" spacing={1}>
                    <IconButton
                      size="small"
                      onClick={() => onEdit(task)}
                      color="primary"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => onDelete(task.id)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      ))}
      {tasks.length === 0 && (
        <Grid item xs={12}>
          <Typography color="text.secondary" align="center">
            No tasks found
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};